// Voice: the Maker for a line's voice, one page body. Who says it, how (typed for an AI voice, recorded on the mic, or
// Silence; a new line starts with whichever of the first two the speaker's voice setting says), then "Start from": Blank and every take of the
// line, newest first. Tapping a take makes it the line's sound and puts how it was made back in the form (its words
// and direction, or Record it). Tapping Blank empties the words for a new take; the take in use stays until the new
// one is made. Then the words, the direction and the Say button ("Say the line" for the first take, "Say it again as V4"
// after; a red Record when recording), and the take big under it: one button that plays it, with a bin to delete it.
// Nothing is confirmed: the line goes onto the timeline at once as a "syncing" line when its voice is asked for (or
// its recording made), and is made behind it (see makeClip.ts), so the page can be left while it is made. A take that
// went wrong is a red tile that makes it again from the same words. The speaker can be changed any time: new takes use
// the new voice, and the line itself changes hands.
// Silence hides Who, the takes, the words and the take under them, and shows how long it lasts instead (a stepper in half
// seconds and the usual lengths, saved as they are changed). Picking it on a spoken line keeps its takes, so Type it or
// Record it brings them back and tapping one gets the speech back. Making a voice on a silent line turns it into a spoken
// one in place. A silent line the picture came with (a placeholder) opens on Type it or Record it, to invite what is said.
// The recorder takes over the whole screen while it runs. A new take plays by itself once it is ready.
import { useEffect, useRef, useState } from "react";
import { useRecorder } from "../../episode/Recorder";
import { useLibrary, voiceOf } from "../../../library/store";
import type { Take } from "../../../model/episode";
import { findAsset } from "../../../model/library";
import { EMOTIONS, type Emotion } from "../../../model/face";
import { MAX_LINE } from "../../../model/speech";
import { MAX_SILENCE, MIN_SILENCE, NEW_SILENCE, addLine, lineAtTime, lineById, faceAt, feelingAt, onScreen, pictureAt, removeTake, setLine, type PaintSource } from "../../../model/timeline";
import { FrameSvg } from "../../../render/FrameSvg";
import { engine } from "../../../audio/playback";
import { audioUrl } from "../../api";
import { Avatar, FitFrame, Icon, Layer, Page, Section, TopBar, fmt, secs, useIdleClock } from "../../common";
import { useFootPlain } from "../../stages/StagePage";
import { RefPicker } from "../../images/ImageMaker";
import { VoiceEditor } from "../../library/AssetPage";
import { MakeRow, ResultFrame, VersionStrip } from "../../maker/Maker";
import { useDraft } from "../../drafts";
import { useTimeline } from "../TimelineLoader";
import { makeClip } from "./makeClip";
import type { PageProps } from "./index";
import { frameWith, refCharacterIds, useCharacters } from "./shared";

type How = "type" | "record" | "silence";
const CHIPS = [0.5, 1, 2, 3, 5];
const STEP = 0.5;
const clamp = (x: number) => Math.min(MAX_SILENCE, Math.max(MIN_SILENCE, Math.round(x / STEP) * STEP));
const label = (s: number) => `${s} s`;
const WAVE = [30, 60, 90, 50, 75, 40, 85, 65, 30, 55, 80, 45, 70, 35, 60, 25]; // a decorative wave on the player

// What a tapped expression adds to the direction.
const SAY: Record<Emotion, string> = { neutral: "calm and flat", happy: "happy and upbeat", angry: "angry", sad: "sad and quiet", surprised: "surprised", smug: "smug, pleased with themselves" };

export function VoiceMaker({ t, id, char, onOpen, onSeek, onError }: {
  t: number; id: string; char?: string; // where the line goes (or is), the id it has or will get, a speaker to start with
  onOpen: PageProps["onOpen"]; onSeek: (t: number) => void; onError: (message: string) => void;
}) {
  const { ep, update } = useTimeline();
  const lib = useLibrary();
  const chars = useCharacters();
  const clock = useIdleClock();
  const clip = lineById(ep, id); // spoken or silent
  const spoken = !!clip && clip.how !== "silent";
  // Who's in the scene: faces placed on the picture at t, and characters the picture was painted with.
  const pic = pictureAt(ep, t); // the picture this line would play over
  const inScene = [...new Set([...onScreen(ep, t), ...(pic ? refCharacterIds(lib, pic.refs) : [])])].filter(c => chars.some(x => x.id === c));
  const [who, setWho] = useState<string | undefined>(char ?? clip?.char ?? inScene[0]);
  // A new line is typed or recorded the way its speaker's voice is set up (AI voice: typed; your own: recorded).
  const howFor = (c: string | undefined): How => (findAsset(lib, c ?? "")?.voiceMode === "ai" ? "type" : "record");
  // A silent line opens on Silence, unless it is the placeholder a picture came with: that opens on the speaker's way.
  const [how, setHow] = useState<How>(clip ? (clip.how === "recorded" ? "record" : clip.how === "typed" ? "type" : clip.placeholder ? howFor(who) : "silence") : howFor(who));
  const [seconds, setSecondsNow] = useState(clip?.how === "silent" ? clip.duration : NEW_SILENCE);
  const [others, setOthers] = useState(false); // the picker of every character is open
  const [voicing, setVoicing] = useState(false); // the speaker's voice page is open
  // The words and direction typed but not made yet are kept, so they survive switching stage (ui/drafts.ts).
  const [text, setText] = useDraft(`line:${id}:text`, clip?.text ?? "");
  const [direction, setDirection] = useDraft(`line:${id}:direction`, clip?.direction ?? "");
  const [asking, setAsking] = useState(false); // the free-text direction box is open
  const [fresh, setFresh] = useState(!clip?.takes?.length); // Blank is picked: the next take is a new one
  const name = findAsset(lib, who ?? "")?.name ?? "them";
  const askDirection = asking || !!direction.trim();
  const voiceOver = !!who && !spoken && how !== "silence" && !faceAt(ep, who, t);

  // A recording is the line (a new one) or a new take of it, straight away.
  const recorder = useRecorder((blob, seconds) => voice({ blob, seconds }), onError);

  function pickWho(c: string) {
    setWho(c);
    if (clip) update(e => setLine(e, clip.id, { char: c }));
    else setHow(howFor(c));
  }

  // How: Type it and Record it only change the form. Silence makes the line silent at once (a new line is added, a spoken
  // one goes quiet but keeps its takes), and picking a take later brings the speech back.
  function pickHow(h: How) {
    if (h === how || making) return;
    stopTake();
    if (h !== "silence") { setHow(h); if (clip?.how === "silent") setFresh(true); return; }
    if (clip?.how === "silent") { setHow(h); return; } // already silent: its length is what it is
    const len = clip ? clamp(clip.duration) : NEW_SILENCE;
    setSecondsNow(len);
    setHow(h);
    if (clip) {
      update(e => setLine(e, clip.id, { how: "silent", status: "ready", error: undefined, audio: undefined, text: undefined, direction: undefined, cues: [], duration: len }));
      return;
    }
    const add = (e: typeof ep) => addLine(e, t, { id, how: "silent", status: "ready", duration: len, cues: [] }, !!lineAtTime(e, t));
    const r = add(ep);
    update(e => (lineById(e, id) ? e : e === ep ? r.ep : add(e).ep));
    onSeek(r.start);
  }
  // The silence's length is saved as it is changed.
  function setSeconds(s: number) {
    setSecondsNow(s);
    if (clip) update(e => setLine(e, id, { duration: s }), { merge: `silence-${id}` });
  }

  // Makes the voice: a new line goes onto the timeline (the playhead goes to its start); an existing one gets a new
  // take. With no source, it is the words and direction as they are on the page.
  function voice(source: { blob: Blob; seconds: number } | { text: string; direction?: string } | null) {
    if (!who) return;
    const from = source ?? { text, direction };
    if ("text" in from && !from.text.trim()) return;
    stopTake();
    const now = lineById(ep, id);
    const r = makeClip(ep, update, { char: who, t, id, ...(now ? { editId: id } : {}), source: from, emotion: feelingAt(ep, who, now ? now.start : t) });
    if (!now && r.start !== undefined) onSeek(r.start);
    autoPlay.current = true;
  }
  // Picking a take makes it the line's sound (its length moves later lines), and puts the way it was made back in the
  // form: its words and direction, or Record it.
  function pickTake(tk: Take) {
    if (!clip) return;
    stopTake();
    setFresh(false);
    update(e => setLine(e, clip.id, { how: tk.text !== undefined ? "typed" : "recorded", status: "ready", error: undefined, audio: tk.audio, duration: tk.duration, cues: tk.cues, text: tk.text, direction: tk.direction }));
    if (tk.text !== undefined) { setHow("type"); setText(tk.text); setDirection(tk.direction ?? ""); } // (a silent line is spoken again)
    else setHow("record");
  }
  // Blank: the form is empty for a new take; the take in use is left alone.
  function pickBlank() {
    stopTake();
    setFresh(true);
    setText("");
    setDirection("");
  }
  // The same words again, from a red tile (a recording can't be: it is recorded again).
  function tryAgain() {
    if (!clip) return;
    if (clip.how === "typed") voice({ text: clip.text ?? "", direction: clip.direction });
    else { stopTake(); recorder.press(); }
  }

  // One take's sound, through the speaker's voice filter. A new take plays once it is ready.
  const [playingTake, setPlayingTake] = useState<string | null>(null);
  const playGen = useRef(0);
  function stopTake() {
    playGen.current++;
    engine.stop();
    setPlayingTake(null);
  }
  async function playTake(tk: Take) {
    if (playingTake === tk.id) return stopTake();
    const my = ++playGen.current;
    try {
      const buf = await engine.load(audioUrl(ep.id, tk.audio));
      if (my !== playGen.current) return;
      await engine.playSample(buf, voiceOf(lib)(who ?? ""));
      setPlayingTake(tk.id);
      setTimeout(() => { if (my === playGen.current) setPlayingTake(null); }, buf.duration * 1000 + 300);
    } catch { onError("Couldn't play that take."); }
  }
  const autoPlay = useRef(false);
  useEffect(() => {
    if (!clip || clip.how === "silent" || clip.status !== "ready" || !autoPlay.current) return;
    autoPlay.current = false;
    setFresh(false); // the new take is the one shown now
    const tk = clip.takes?.find(x => x.audio === clip.audio);
    if (tk) void playTake(tk);
  }, [clip?.status, clip?.audio]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { playGen.current++; engine.stop(); }, []);

  // Full-screen recording: the picture at t, the count-in and the level meter.
  const recording = recorder.busy && (() => {
    const r = recorder.rec;
    return (
      <Layer>
        <Page dark className="take">
          <TopBar onBack={() => recorder.cancel()} title={`${name} speaks`} />
          <FitFrame>
            <FrameSvg id="tl-rec" frame={frameWith(ep, clip?.start ?? t, clock)} />
            {r.phase === "count" && <div className="count" key={r.n}>{r.n}</div>}
            {r.phase === "recording" && <div className="rec-pill"><span className="dot" /> Recording {fmt(recorder.elapsed)}</div>}
          </FitFrame>
          <div className="fs-tools center">
            <div className="levels">{recorder.levels.map((l, i) => <span key={i} style={{ height: `${8 + (r.phase === "recording" ? l : 0) * 92}%` }} />)}</div>
            <button className={`record${r.phase === "recording" ? " on" : ""}`} onClick={() => recorder.press()} aria-label={r.phase === "recording" ? "Stop recording" : "Cancel"}><span /></button>
            <p className="fs-hint">{r.phase === "recording" ? "Say the line, then tap to stop" : "Get ready…"}</p>
          </div>
        </Page>
      </Layer>
    );
  })();

  const takes = clip?.takes ?? [];
  const making = clip?.status === "syncing";
  const failed = clip?.status === "error" && !making;
  const on = clip && spoken ? takes.findIndex(tk => tk.audio === clip.audio) : -1;
  const picked = !fresh && on >= 0 ? takes[on] : undefined;
  const next = `V${takes.length + 1}`; // what the next take will be called
  // Say is the yellow button until a take exists and the words are as that take was made; then it is a white second go.
  const unchanged = !!picked && picked.text === text.trim() && (picked.direction ?? "") === direction.trim();
  const sayPrimary = how === "type" && !making && !!text.trim() && !unchanged;
  useFootPlain(sayPrimary);

  return (
    <>
      {recording}
      {how !== "silence" && <Section title="Who says it" right={who ? <button className="mk-link" onClick={() => { stopTake(); setVoicing(true); }}>{name}'s voice <Icon name="chevron" size={14} /></button> : undefined}>
        <div className="mk-who" role="radiogroup" aria-label="Who says it">
          {[...inScene, ...(who && !inScene.includes(who) ? [who] : [])].map(c => (
            <button key={c} role="radio" aria-checked={who === c} className={`mk-who-one${who === c ? " on" : ""}`} onClick={() => pickWho(c)}>
              <span className="mk-who-pic"><Avatar assetId={c} size={58} /></span>
              <span>{findAsset(lib, c)?.name ?? "?"}</span>
            </button>
          ))}
          <button className="mk-who-one other" aria-label="Someone else" onClick={() => setOthers(true)}>
            <span className="mk-who-pic"><Icon name="plus" size={20} /></span>
            <span>Other</span>
          </button>
        </div>
        {voiceOver && (
          <>
            <p className="hint tl-note">{name} isn't in the picture here, so this will be a voice-over.</p>
            {pic && (
              <button className="btn small soft" onClick={() => onOpen("picture", { t: pic.t, editId: pic.id, step: "faces" })}>
                <Icon name="person" size={16} />Put {name} in this picture
              </button>
            )}
          </>
        )}
      </Section>}

      <div className="mk-seg" role="radiogroup" aria-label="How">
        {([["type", "Type it", "keyboard"], ["record", "Record it", "mic"], ["silence", "Silence", "silence"]] as const).map(([v, l, icon]) => (
          <button key={v} role="radio" aria-checked={how === v} className={how === v ? "on" : ""} onClick={() => pickHow(v)}><Icon name={icon} size={16} />{l}</button>
        ))}
      </div>

      {how === "silence" ? (
        <Section title="How long" hint="Nobody speaks. Everything after it moves later.">
          <div className="tl-stepper">
            <button className="icon-btn" disabled={seconds <= MIN_SILENCE} onClick={() => setSeconds(clamp(seconds - STEP))} aria-label="Shorter"><Icon name="minus" /></button>
            <b aria-live="polite">{label(seconds)}</b>
            <button className="icon-btn" disabled={seconds >= MAX_SILENCE} onClick={() => setSeconds(clamp(seconds + STEP))} aria-label="Longer"><Icon name="plus" /></button>
          </div>
          <div className="tl-chips" role="radiogroup" aria-label="Silence length">
            {CHIPS.map(c => (
              <button key={c} role="radio" aria-checked={seconds === c} className={`tl-chip${seconds === c ? " on" : ""}`} onClick={() => setSeconds(c)}>{label(c)}</button>
            ))}
          </div>
        </Section>
      ) : <>
      {(takes.length > 0 || making || failed) && (
        <VersionStrip disabled={making}
          fresh={{ label: "Blank", on: fresh, onPick: pickBlank }}
          making={making ? { label: next, word: "Making" } : undefined}
          failed={failed ? { label: next, onRetry: tryAgain } : undefined}
          versions={takes.map((tk, i) => ({
            id: tk.id, label: `V${i + 1}`, wave: true, on: !fresh && i === on,
            sub: <>{tk.text === undefined && <Icon name="mic" size={12} />}{secs(tk.duration)}</>,
            title: tk.text !== undefined ? `“${tk.text}”${tk.direction ? ` · ${tk.direction}` : ""}` : "Recorded",
          })).reverse()}
          onPick={tid => { const tk = takes.find(x => x.id === tid); if (tk) pickTake(tk); }} />
      )}

      {how === "type" ? (
        <>
          <Section title="What they say">
            <textarea className="input mk-bubble" rows={3} maxLength={MAX_LINE} value={text} disabled={making} aria-label="What they say" placeholder="Type the line…" onChange={e => setText(e.target.value)} />
            <div className="mk-pills" role="group" aria-label="Quick expressions">
              {EMOTIONS.map(e => {
                const on = direction.includes(SAY[e]);
                return (
                  <button key={e} className={`mk-pill${on ? " on" : ""}`} aria-pressed={on} disabled={making}
                    onClick={() => setDirection(on ? direction.replace(SAY[e], "").replace(/^[,\s]+|[,\s]+$/g, "").replace(/,\s*,/g, ",") : (direction.trim() ? `${direction.trim().replace(/,$/, "")}, ` : "") + SAY[e])}>{e[0]!.toUpperCase() + e.slice(1)}</button>
                );
              })}
              {!askDirection && <button className="mk-pill add" disabled={making} onClick={() => setAsking(true)}><Icon name="plus" size={14} /> Direction</button>}
            </div>
            {askDirection && <textarea className="input" rows={2} maxLength={200} value={direction} disabled={making} aria-label="Direction" placeholder="Tell the voice how to say it, like “whisper it, like a secret”." onChange={e => setDirection(e.target.value)} />}
          </Section>
          <MakeRow plain={!sayPrimary} disabled={making || !who || !text.trim()} onClick={() => voice(null)}>
            <Icon name="sparkle" /> {takes.length ? `Say it again as ${next}` : "Say the line"}
          </MakeRow>
        </>
      ) : (
        <Section>
          <p className="hint">Press Record and say the line into the mic.</p>
          <MakeRow red disabled={making || !who} onClick={() => { stopTake(); recorder.press(); }}>
            <span className="rec-dot" /> {takes.length ? `Record ${next}` : "Record"}
          </MakeRow>
        </Section>
      )}

      <ResultFrame className="mk-playerbox" empty={picked ? undefined : `${next} plays here`}
        busy={making ? { message: `Making ${next}…`, ms: clip?.how === "typed" ? 12_000 : 8_000 } : undefined}
        onDelete={picked && takes.length > 1 && clip ? () => { stopTake(); update(e => removeTake(e, clip.id, picked.id)); } : undefined} deleteLabel={`Delete V${on + 1}`}>
        {picked && (
          <button className="mk-player" aria-label={playingTake === picked.id ? `Stop V${on + 1}` : `Play V${on + 1}`} title={picked.text !== undefined ? `“${picked.text}”` : "Recorded"} onClick={() => void playTake(picked)}>
            <span className="mk-player-play"><Icon name={playingTake === picked.id ? "stop" : "play"} size={20} /></span>
            <span className="mk-player-wave" aria-hidden="true">{WAVE.map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</span>
            <span className="mk-player-text">V{on + 1} · {picked.duration.toFixed(1)} s</span>
          </button>
        )}
      </ResultFrame>
      </>}

      {others && (
        <RefPicker group={{ key: "cast", title: "Characters", kind: "character", items: chars.filter(c => c.picture?.src).map((c): PaintSource => ({ src: c.picture.src, name: c.name, kind: "character" })) }}
          refs={chars.filter(c => c.id === who).map((c): PaintSource => ({ src: c.picture.src, name: c.name, kind: "character" }))}
          onToggle={r => { const c = chars.find(x => x.picture.src === r.src); if (c) pickWho(c.id); setOthers(false); }} onClose={() => setOthers(false)} />
      )}
      {voicing && who && <VoiceEditor assetId={who} onBack={() => { setVoicing(false); if (!spoken) setHow(howFor(who)); }} />}
    </>
  );
}
